/**
 * Authentication API using Supabase
 *
 * Migrated from backend API to Supabase Auth
 */

import { supabase } from '../lib/supabase/client';
import { LogService } from './logService';
import type { User, Session } from '@supabase/supabase-js';

// Only log in development mode
const isDev = import.meta.env.DEV;
const devLog = (message: string, ...args: unknown[]) => {
  if (isDev) console.log(message, ...args);
};

// Storage key used by Supabase client (must match secureTokenStorage prefix + key)
const STORAGE_PREFIX = 'wealthhub-secure-';
const STORAGE_KEY = 'wealthhub-auth';

// ===== TYPE DEFINITIONS =====

export interface RegisterData {
  email: string;
  password: string;
  username?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface UserProfile {
  id: string;
  email: string;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  emailVerified: boolean;
  subscriptionTier: string;
  createdAt: string;
}

export interface AuthResponse {
  user: UserProfile;
  session: Session;
}

/**
 * Helper: Convert Supabase User to UserProfile
 */
function supabaseUserToProfile(user: User, extendedProfile?: {
  username?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
  subscription_tier?: string;
} | null): UserProfile {
  return {
    id: user.id,
    email: user.email!,
    username: extendedProfile?.username ?? user.user_metadata?.username ?? null,
    displayName: extendedProfile?.display_name ?? user.user_metadata?.display_name ?? null,
    avatarUrl: extendedProfile?.avatar_url ?? user.user_metadata?.avatar_url ?? null,
    emailVerified: !!user.email_confirmed_at,
    subscriptionTier: extendedProfile?.subscription_tier ?? user.user_metadata?.subscription_tier ?? 'free',
    createdAt: user.created_at,
  };
}

/**
 * Register new user
 */
export async function register(data: RegisterData): Promise<AuthResponse> {
  const { email, password, username } = data;

  // Log signup attempt
  LogService.info('Supabase Auth', `Attempting signup for ${email}`);

  try {
    // Sign up with Supabase
    const { data: authData, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username: username || email.split('@')[0],
        },
      },
    });

    if (error) {
      // Log detailed error information
      LogService.error('Supabase Auth', 'Signup failed - Supabase returned error', {
        error: {
          message: error.message,
          status: error.status,
          code: error.code,
          name: error.name,
        },
        context: {
          email,
          hasUsername: !!username,
          timestamp: new Date().toISOString(),
        },
        // Include full error object for debugging
        fullError: JSON.stringify(error, null, 2),
      });

      throw new Error(error.message);
    }

    if (!authData.user || !authData.session) {
      // Log missing user/session
      LogService.error('Supabase Auth', 'Signup failed - No user or session returned', {
        hasUser: !!authData.user,
        hasSession: !!authData.session,
        email,
        timestamp: new Date().toISOString(),
      });

      throw new Error('Registration failed - no user or session returned');
    }

    // Log success
    LogService.success('Supabase Auth', `Signup successful for ${email}`, {
      userId: authData.user.id,
      emailVerified: !!authData.user.email_confirmed_at,
    });

    return {
      user: supabaseUserToProfile(authData.user),
      session: authData.session,
    };
  } catch (error: any) {
    // Catch any unexpected errors (network issues, etc.)
    LogService.critical('Supabase Auth', 'Unexpected error during signup', {
      error: {
        message: error?.message || 'Unknown error',
        name: error?.name,
        stack: error?.stack,
      },
      context: {
        email,
        timestamp: new Date().toISOString(),
      },
    });

    throw error;
  }
}

/**
 * Login user
 */
export async function login(data: LoginData): Promise<AuthResponse> {
  const { email, password } = data;

  // Sign in with Supabase
  const { data: authData, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw new Error(error.message);
  }

  if (!authData.user || !authData.session) {
    throw new Error('Login failed - no user or session returned');
  }

  return {
    user: supabaseUserToProfile(authData.user),
    session: authData.session,
  };
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  const { error } = await supabase.auth.signOut();

  if (error) {
    LogService.error('AuthAPI', 'Logout error', { error: error.message });
    throw new Error(error.message);
  }
}

/**
 * Resend the verification email for an account that has signed up but not
 * yet clicked the link in their inbox. Wraps Supabase's `auth.resend` with
 * a normalized error path so callers can `mapAuthError(err)` consistently.
 *
 * Rate limiting is enforced server-side by Supabase (typically 1 email per
 * 60 seconds per address); UIs should disable the resend button between
 * clicks and show a countdown.
 */
export async function resendVerificationEmail(email: string): Promise<void> {
  LogService.info('AuthAPI', `Resending verification email for ${email}`);
  const { error } = await supabase.auth.resend({ type: 'signup', email });
  if (error) {
    LogService.error('AuthAPI', 'Resend verification failed', { error: error.message });
    throw new Error(error.message);
  }
}

/**
 * Cheap synchronous check: does the current cached user have a verified
 * email? Useful for AuthGuard / route-level decisions where we don't want
 * to round-trip Supabase. The store keeps `user.emailVerified` in sync via
 * the auth listener (TOKEN_REFRESHED + USER_UPDATED).
 */
export function isEmailVerified(user: UserProfile | null): boolean {
  return !!user?.emailVerified;
}

/**
 * Step 1 of the password reset flow: ask Supabase to email the user a
 * recovery link. The email contains a magic URL that lands on
 * `/auth/reset-password` with `?type=recovery&access_token=…` in the
 * fragment, which Supabase auto-detects via `detectSessionInUrl: true`
 * (see lib/supabase/client.ts).
 *
 * We deliberately do NOT differentiate between "email exists" and "email
 * unknown" in the success path — surfacing that distinction here would be
 * a user-enumeration vulnerability. Supabase already returns success
 * regardless; we just normalise the error path so callers can
 * `mapAuthError(err)` consistently.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  LogService.info('AuthAPI', `Password reset requested for ${email}`);
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/reset-password`,
  });
  if (error) {
    LogService.error('AuthAPI', 'Password reset request failed', { error: error.message });
    throw new Error(error.message);
  }
}

/**
 * Step 2 of the password reset flow: set the new password. Supabase's
 * `updateUser` knows which user to target because the recovery link
 * established a temporary session via `detectSessionInUrl`. The session
 * is short-lived; if the user takes too long, this throws and the page
 * sends them back to step 1.
 */
export async function confirmPasswordReset(newPassword: string): Promise<void> {
  LogService.info('AuthAPI', 'Confirming password reset');
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) {
    LogService.error('AuthAPI', 'Password reset confirmation failed', { error: error.message });
    throw new Error(error.message);
  }
}

/**
 * Get current user profile with extended data from user_profiles table
 */
export async function getProfile(): Promise<UserProfile> {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) {
    throw new Error(error.message);
  }

  if (!user) {
    throw new Error('No user found');
  }

  // Try to get extended profile data from user_profiles table
  let extendedProfile = null;
  try {
    const { data: profileData, error: profileError } = await supabase
      .from('user_profiles')
      .select('username, display_name, avatar_url, subscription_tier')
      .eq('id', user.id)
      .single();

    if (!profileError && profileData) {
      extendedProfile = profileData;
      devLog('[AuthAPI] Extended profile loaded:', {
        hasUsername: !!profileData.username,
        hasDisplayName: !!profileData.display_name,
        hasAvatar: !!profileData.avatar_url
      });
    } else if (profileError) {
      devLog('[AuthAPI] Could not load extended profile:', profileError.message);
    }
  } catch (err) {
    devLog('[AuthAPI] Error fetching extended profile:', err);
  }

  return supabaseUserToProfile(user, extendedProfile);
}

/**
 * Fast session retrieval directly from localStorage
 * This avoids waiting for Supabase client initialization/network calls
 */
function getSessionFromStorage(): Session | null {
  try {
    // Read from the correct prefixed key
    const stored = localStorage.getItem(STORAGE_PREFIX + STORAGE_KEY);
    if (!stored) {
      return null;
    }

    // Decode base64 (matching secureTokenStorage encoding)
    let decoded: string;
    try {
      decoded = atob(stored);
      // Verify it's valid JSON after decoding
      JSON.parse(decoded);
    } catch {
      // Not valid base64 or the decoded result isn't JSON
      // Try treating it as raw JSON
      try {
        JSON.parse(stored);
        decoded = stored;
      } catch {
        devLog('[AuthAPI] Stored value is neither base64 nor valid JSON');
        return null;
      }
    }

    const parsed = JSON.parse(decoded);

    // Handle potential wrapped session format (e.g., { currentSession: {...} })
    const session = parsed?.currentSession || parsed;

    if (!session?.access_token) {
      return null;
    }

    // Check if token is expired - but don't reject if refresh token exists
    // Supabase will auto-refresh if access token is expired but refresh token is valid
    const parts = session.access_token.split('.');
    if (parts.length === 3) {
      try {
        const payload = JSON.parse(atob(parts[1]));
        const exp = payload.exp;
        if (exp && Date.now() >= exp * 1000) {
          // Token is expired - check if we have a refresh token
          if (session.refresh_token) {
            devLog('[AuthAPI] Access token expired but refresh token available, Supabase will refresh');
            // Still return the session - Supabase will handle the refresh
          } else {
            devLog('[AuthAPI] Token expired and no refresh token');
            return null;
          }
        }
      } catch (e) {
        // Can't parse token, continue - token might still be valid
      }
    }

    // Reconstruct a Session-like object from storage
    return session as Session;
  } catch (err) {
    devLog('[AuthAPI] Error reading session from storage:', err);
    return null;
  }
}

/**
 * Get current session (with fast localStorage fallback)
 */
export async function getSession(): Promise<Session | null> {
  // Fast path: Try localStorage first
  const storedSession = getSessionFromStorage();
  if (storedSession) {
    devLog('[AuthAPI] Using session from localStorage (fast path)');
    return storedSession;
  }

  // Slow path: Use Supabase client with timeout
  try {
    devLog('[AuthAPI] Falling back to Supabase client for session');
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Session timeout')), 10000); // 10 seconds for slow networks
    });

    const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise]);

    if (error) {
      LogService.error('AuthAPI', 'Session error', { error: error.message });
      return null;
    }

    return session;
  } catch (err: any) {
    devLog('[AuthAPI] getSession error:', err.message);
    return null;
  }
}

/**
 * Refresh access token (handled automatically by Supabase)
 */
export async function refreshToken(): Promise<Session | null> {
  const {
    data: { session },
    error,
  } = await supabase.auth.refreshSession();

  if (error) {
    LogService.error('AuthAPI', 'Token refresh error', { error: error.message });
    return null;
  }

  return session;
}
